import {
  AnalyticsApi,
  AnalyticsEvent,
  ConfigApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';

export class AzureApplicationInsightsAnalyticsApi implements AnalyticsApi {
  private readonly appInsights: ApplicationInsights;
  private userEntityRef?: string;

  private constructor(
    appInsights: ApplicationInsights,
    userEntityRef?: string,
  ) {
    this.appInsights = appInsights;
    this.userEntityRef = userEntityRef;
  }

  static fromConfig(
    configApi: ConfigApi,
    options: {
      identityApi?: IdentityApi;
      history?: any;
    },
  ): AzureApplicationInsightsAnalyticsApi {
    const connectionString = configApi.getString('connectionString');
    const samplingPercentage = configApi.getOptionalNumber('samplingPercentage') ?? 100;
    const enableAutoRouteTracking = configApi.getOptionalBoolean('enableAutoRouteTracking') ?? true;

    const reactPlugin = new ReactPlugin();
    const clickPlugin = new ClickAnalyticsPlugin();

    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        samplingPercentage,
        enableAutoRouteTracking,
        extensions: [reactPlugin, clickPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: options.history,
          },
          [clickPlugin.identifier]: {
            autoCapture: true,
          },
        },
      },
    });

    appInsights.loadAppInsights();

    const instance = new AzureApplicationInsightsAnalyticsApi(appInsights);

    if (options.identityApi) {
      options.identityApi.getBackstageIdentity()
        .then(identity => {
          instance.setUser(identity.userEntityRef);
        })
        .catch(() => {
          // If identity is not available, we can still use the instance without user context
        });
    }

    return instance;
  }

  setUser(userEntityRef: string) {
    this.userEntityRef = userEntityRef.split('/')[1];
    this.appInsights.setAuthenticatedUserContext(this.userEntityRef);
  }

  captureEvent(event: AnalyticsEvent) {
    const { action, subject, value, attributes, context } = event;

    const properties: Record<string, unknown> = {
      subject,
      value,
      user: this.userEntityRef,
      ...attributes,
      ...Object.fromEntries(
        Object.entries(context || {}).map(([k, v]) => [`context.${k}`, v]),
      ),
    };

    // console.debug(`Tracking event: ${action}:${subject}`, properties);
    this.appInsights.trackEvent({ name: `${action}:${subject}` }, properties);
  }
}
